import React, { useState } from 'react';
import { LinkItem } from '../../types/familyLinks';

const OCEAN_PALETTE = [
  '#0050ef', '#1ba1e2', '#00aba9', '#2d5fa8',
  '#5a4a8a', '#6a00ff', '#0078d7', '#008272',
];

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

function getColor(url: string, id: string): string {
  return OCEAN_PALETTE[hashString(url + id) % OCEAN_PALETTE.length];
}

function getDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function getFaviconUrl(url: string): string | null {
  try {
    return `https://icon.horse/icon/${new URL(url).hostname}`;
  } catch {
    return null;
  }
}

function getFaviconFallbackUrl(url: string): string | null {
  try {
    return `https://www.google.com/s2/favicons?domain=${new URL(url).hostname}&sz=128`;
  } catch {
    return null;
  }
}

interface LinkTileProps {
  link: LinkItem;
  onEdit: (link: LinkItem) => void;
  onDelete: (link: LinkItem) => void;
}

const LinkTile: React.FC<LinkTileProps> = ({ link, onEdit, onDelete }) => {
  const [faviconStep, setFaviconStep] = useState(0);
  const color = getColor(link.url, link.id);
  const initial = link.name.charAt(0).toUpperCase();

  const favicon = faviconStep === 0
    ? getFaviconUrl(link.url)
    : faviconStep === 1
      ? getFaviconFallbackUrl(link.url)
      : null;

  return (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      className="family-links-tile animated"
      style={{ background: color }}
    >
      <div className="family-links-tile-inner">
        <div className="family-links-tile-face family-links-tile-face-front">
          <div className="family-links-tile-favicon">
            {favicon ? (
              <img
                src={favicon}
                alt=""
                onError={() => setFaviconStep(s => s + 1)}
              />
            ) : (
              <div className="family-links-tile-favicon-fallback">
                {initial}
              </div>
            )}
          </div>
          <div className="family-links-tile-title">{link.name}</div>
        </div>
        <div className="family-links-tile-face family-links-tile-face-back">
          <div className="family-links-tile-title" style={{ fontSize: 14, marginTop: 0 }}>
            {link.name}
          </div>
          <div className="family-links-tile-domain">{getDomain(link.url)}</div>
        </div>
      </div>
      <div className="family-links-tile-actions">
        <button
          type="button"
          className="family-links-tile-action-btn"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEdit(link); }}
          aria-label="Edit"
        >
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M11.7 1.3a1 1 0 0 1 1.4 0l1.6 1.6a1 1 0 0 1 0 1.4L5.5 13.5l-3.5.5.5-3.5L11.7 1.3zm-7.2 9.8L11.6 4l-.7-.7-7.1 7.1-.2 1 .9-.3z"/>
          </svg>
        </button>
        <button
          type="button"
          className="family-links-tile-action-btn"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(link); }}
          aria-label="Delete"
        >
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M4 1h8v2h3v1h-1v11a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4H1V3h3V1zm1 3v10h6V4H5zm1 1h1v8H6V5zm3 0h1v8H9V5z"/>
          </svg>
        </button>
      </div>
    </a>
  );
};

export default LinkTile;
